import { FormikErrors } from '../Lib/Logic';
import { Row } from '../UI/Components/DetailsListBox/DetailsListBox';
import { DataTableConfig } from './datatable.types';
import { ActionStatus } from './slice-types';
import { FeedBackType } from './ui-feedback.types';


export const MEDIA_TYPES = ['image', 'video'] as const;

export type MediaType = typeof MEDIA_TYPES[number];

export type FormikFieldSetter<T> = (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<T>>;

export interface MediaItem {
    type: MediaType;
    large: string;
    caption?: string;
    description?: string;
    small?: string,
    id: number | string;
}

export interface DomainTableConfig<VM extends Record<string, any>> {
    tableConfig: DataTableConfig<VM>;
    response: { status: ActionStatus; error?: string };
}

export type DetailItem = {
    label: string;
    value: string;
    onClick?: () => void;
    link?: string
}

export type DetailSection = {
    sectionFn: () => DetailList | DetailItem[],
    legend: string,
    type: 'box' | 'list'
}

export type DetailList = {
    headers: string[];
    rows: Row[]
}

export interface ToastData {
    message: string;
    type?: FeedBackType;
    duration?: number;
}

export interface AlertData {
    title?: string;
    message: string;
    type?: FeedBackType;
}

export interface DownloadUIFile {
    type : string;
    label: string;
    handlers: {
        generate?: () => Promise<void>|void;
        export?: () => Promise<void>|void;
    }
}

export interface GenerateUIFile {
    type : string;
    label: string;
    handler:  (form? : Record<string, any>) => Promise<void>|void;
}

export interface ExportUIData {
    toGenerate: GenerateUIFile[];
    toDownload: DownloadUIFile[];
    id : number;
    noFormDocTypes? : string[]
}

export type AppEnum = {
    key: string,
    value: string|number,
    label: string,
    children?: AppEnum[]
    parent?: AppEnum,
    min?: number,
    max?: number,
    group?: string
}

export type WidgetFormat = 'List' | 'Card';

export type AppEnumsRecord = Record<
    | 'PropertyCategoryEnum'
    | 'PropertyTypeEnum'
    | 'PropertyFeatureCategoryEnum'
    | 'PropertyFeatureTypeEnum'
    | 'PropertyUsageEnum'
    | 'PriceRangeEnum'
    | 'AdSourceCategoryEnum'
    | 'AdSourceTypeEnum'
    | 'AccessTypeEnum'
    | 'PublicationCategoryEnum'
    | 'CivilityEnum'
    | 'ContactTypeEnum'
    | 'NeedStatusEnum'
    | 'OperationTypeEnum'
    | 'MandateTypeEnum'
    | 'PropertyAvailabilityEnum'
    | 'PropertyConditionEnum'
    | 'MatchingStatusEnum'
    | 'TaskCategoryEnum'
    | 'ActivityStatusEnum'
    | 'PlanningTypeEnum'
    | 'ActivityPriorityEnum'
    | 'PropertyStandingEnum'
    | 'ResourceTypeEnum'
    | 'UserRoleEnum'
    | 'UserPositionEnum'
    | 'DocumentCategoryEnum'
    | 'DocumentTypeEnum'
    | 'LeaseTypeEnum'
    | 'LeaseOptionEnum',
    AppEnum[]
>;

export const BREAD_CRUMPS_LABELS: Record<string, string> = {
    agents: 'Agents',
    users: 'Agents',
    contacts: 'Contacts',
    mandates: 'Mandats',
    transactions: 'Transactions',
    activities: 'Activités',
    needs: 'Besoins',
    tasks: 'Tâches',
    wishes: "Voeux",
    results: 'Résultats',
    properties: 'Biens',
    publications: 'Annonces',
    agenttargets: 'Objectifs Agent',

    documents: 'Documents',
    upload: 'Téléversé',
    rentalLease: 'Bail Locatif',
    proLease: 'Bail Pro.',
    visitReport: 'Bon de visite',
    rentalComitment: 'Engangement Locatif',
    propertySheet: 'Fiche bien',
    managementMandate: 'Mandat de gestion',
    searchMandate: 'Mandat de rech. biens',
    salesMandate: 'Mandat de ventes',
    prospectingManual: 'Manuel prospection',
    marketingManuel: 'Manuel commercialisation',
    rentalRepairs: 'Réparations Locatives',
    tarifs: 'Tarifs',
    leases: 'Baux',
    permissions: 'Permissions',
    plannings: 'Plannings',
    matchings: 'Rapprochements',
    new: 'Nouveau',
    edit: 'Modifier',
    projects: 'Projets',
    settings: 'Paramètres',
    login: 'Connexion',
    selector: 'Choix'
};

export enum RESOURCE_TYPES {
    Contact = 'Contact',
    Activity = 'Activity',
    Mandate = 'Mandate',
    Matching = 'Matching',
    Need = 'Need',
    Permission = 'Permission',
    Planning = 'Planning',
    Property = 'Property',
    Publication = 'Publication',
    AgentTarget = 'AgentTarget',
    Task = 'Task',
    Transaction = 'Transaction',
    User = 'User',
    Document = 'Document',
    Lease = 'Lease',
    BirthWish = 'BirthWish',
    Result = 'Result',
}

export enum DOMAIN_TYPES {
    Contact = 'Contact',
    Activity = 'Activity',
    Mandate = 'Mandate',
    Matching = 'Matching',
    Need = 'Need',
    Permission = 'Permission',
    Planning = 'Planning',
    Property = 'Property',
    Task = 'Task',
    Transaction = 'Transaction',
    User = 'User',
    Document = 'Document',
    Lease = 'Lease',
    BirthWish = 'BirthWish',
    Result = 'Result',
    Challenge = 'Challenge',
    Publication = 'Publication',
    AgentTarget = 'AgentTarget'
}


export type DomainType = keyof typeof DOMAIN_TYPES;

export type DomainRedux = DomainType | 'MediaFile';


export type SelectOptionType = { value: string | number; label: string };

export type AccessRightKey = 'read' | 'write' | 'delete' | 'restore' | 'export' | 'execute' | 'audit';

export type ActionGroups = {
    header: UIAction[];
    sub: UIAction[];
    media: UIAction[];
};

export type UIActionType =
    | 'navigation' // changes page / navigates to a different context
    | 'execute'    // triggers a direct backend operation
    | 'context'    // stays in the current context (e.g. modal, inline action)
    | 'document'  // related to document handling (PDF, Excel, etc.)
    | 'default';

export type UIActionIcon =
    | 'FaTrash'
    | 'FaTrashAlt'
    | 'FaRedo'
    | 'FaEdit'
    | 'FaPlusCircle'
    | 'FaFilePdf'
    | 'FaLock';

export interface UIAction {
    key: string | number;
    label: string;
    onClick?: (id?: number) => void;
    hidden?: boolean;
    disabled?: boolean;
    responsive?: boolean;
    placement: keyof ActionGroups;
    icon: UIActionIcon,
    type: UIActionType;
    loading?: boolean;
    tooltip?: string;
}